import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CloudOff, LogOut, RefreshCw } from 'lucide-react';

import { useAuth } from './useAuth';

/**
 * The whole staff website while the venue's EclipseGG service is suspended
 * (EclipseGG platform, 2026-10-19: "warn, then suspend"). The server refuses
 * every staff route with 403 `venue_suspended` until the seller reactivates
 * the venue, so this replaces every page — and every page's polling with it —
 * instead of a loader retrying for ever behind a refusal that will not change.
 *
 * Fixed, translated words only. The seller's reason is internal and never sent
 * to a venue; the venue is told whom to call, nothing more.
 *
 * Neither button is a dead end. Try again runs the boot once more — the cookie
 * was kept on purpose, so after a reactivation it lands straight back in the
 * app, no password typed. Sign out is a public route, so it works while
 * suspended, and leads to the sign-in page (which then names the suspension
 * itself, if anyone tries).
 */
export function SuspendedScreen() {
  const { retry, signOut } = useAuth();
  const [retrying, setRetrying] = useState(false);

  async function handleRetry(): Promise<void> {
    setRetrying(true);
    try {
      await retry();
    } finally {
      setRetrying(false);
    }
  }

  return (
    <SuspendedView
      retrying={retrying}
      onRetry={() => void handleRetry()}
      onSignOut={() => void signOut()}
    />
  );
}

export interface SuspendedViewProps {
  /** Try again is running: the button holds, so one press is one attempt. */
  retrying: boolean;
  onRetry: () => void;
  onSignOut: () => void;
}

/** The screen itself, stateless so its states can be rendered in tests. */
export function SuspendedView({ retrying, onRetry, onSignOut }: SuspendedViewProps) {
  const { t } = useTranslation();

  return (
    // The sign-in card's frame: the blueprint grid on <body> is the backdrop,
    // and `m-auto` centres while there is room (see LoginPage).
    <div className="flex h-full overflow-y-auto p-4">
      <section
        data-venue-suspended
        className="m-auto w-full max-w-sm rounded-card border border-subtle bg-raised p-6 text-center shadow-xl"
      >
        <CloudOff className="mx-auto mb-4 h-10 w-10 text-content-muted" aria-hidden />
        <h1 className="mb-2 text-xl font-semibold text-content">
          {t('venueService.suspendedTitle')}
        </h1>
        <p className="mb-6 text-sm text-content-secondary">{t('venueService.suspendedBody')}</p>

        <div className="flex flex-col gap-2">
          <button
            type="button"
            data-action="retry"
            onClick={onRetry}
            disabled={retrying}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-primary px-4 py-2 font-medium text-primary-fg transition-colors hover:bg-primary-hover disabled:opacity-60"
          >
            <RefreshCw className={`h-4 w-4 ${retrying ? 'animate-spin' : ''}`} aria-hidden />
            {t('venueService.tryAgain')}
          </button>
          <button
            type="button"
            data-action="sign-out"
            onClick={onSignOut}
            className="flex w-full items-center justify-center gap-2 rounded-full border border-subtle px-4 py-2 font-medium text-content-secondary transition-colors hover:bg-hover hover:text-content"
          >
            <LogOut className="h-4 w-4" aria-hidden />
            {t('auth.signOut')}
          </button>
        </div>
      </section>
    </div>
  );
}
